/**
 * useVatClassification — SW-8538
 *
 * Reads the user's onboarding-survey answer and classifies them as
 * "business" or "consumer" for the VAT checkout flow. Safe defaults:
 * any missing/unknown value falls through to "consumer" (charges VAT).
 *
 * Survey answer precedence:
 *   1. `userMetadata.survey.responses["writing-journey"]` — IMPROVE_ONBOARDING_V1
 *   2. `userMetadata.survey.responses.writerExperience`   — FEB_2025 cohort
 *
 * See docs/vat-project/SW-8538-PRODUCTION-PLAN.md §5.
 */
import useUserMetadata from "./useUserMetadata";

// Exact-match (case-sensitive) set — any other value becomes "consumer".
const BUSINESS_VALUES = new Set([
  "professional",
  "aspiring-professional",
  "PROFESSIONAL",
  "ASPIRING_PROFESSIONAL",
]);

/**
 * Pure function mapping a raw survey answer to a classification.
 * Used by the hook and exported standalone for unit-testability.
 *
 * @param {unknown} raw — raw value pulled from Firestore userMetadata
 * @returns {"business" | "consumer"}
 */
export function classifySurveyAnswer(raw) {
  if (typeof raw !== "string" || raw === "") return "consumer";
  return BUSINESS_VALUES.has(raw) ? "business" : "consumer";
}

/**
 * Hook form — reads userMetadata via SWR and returns the derived
 * classification plus a source label for analytics/audit.
 *
 * @returns {{
 *   classification: "business" | "consumer",
 *   source: "survey_v1" | "survey_feb_2025" | "default_consumer",
 *   isLoading: boolean,
 * }}
 */
export default function useVatClassification() {
  const { userMetadata, isLoading } = useUserMetadata();

  const responses = userMetadata?.survey?.responses;
  const writingJourney = responses?.["writing-journey"];
  const writerExperience = responses?.writerExperience;

  let classification = "consumer";
  let source = "default_consumer";

  if (typeof writingJourney === "string" && writingJourney !== "") {
    classification = classifySurveyAnswer(writingJourney);
    source = "survey_v1";
  } else if (typeof writerExperience === "string" && writerExperience !== "") {
    classification = classifySurveyAnswer(writerExperience);
    source = "survey_feb_2025";
  }

  return { classification, source, isLoading };
}
